/**
 * Execution-path dataset: one record per initiative in the Execute portfolio.
 *
 * Lifted out of EAInitiativeCommandCenterPage, which previously held these as file-local
 * mocks and rendered the same content for every :initiativeId. Records are keyed by
 * opportunity id so the command center resolves the initiative it was asked for.
 *
 * Dates use the demoDates helpers (offsets from a today-anchored date) rather than literal
 * months, so the demo never goes stale. The design handoff's dataset hardcoded 2026 dates;
 * the offsets below reproduce the same relative story.
 *
 * A use case proceeded from Evaluate that has no authored record here gets a pending
 * scaffold from makePending() — status "Approval Pending", first approval step current,
 * empty checklist.
 *
 * EPM WIRING: where the stage execution-plan pipeline has actually run for a use case
 * (adapters/stage300ExecutionPlan), a pending scaffold takes its milestones and risk register
 * from that plan instead of deriving them from the evaluation brief. Authored fixture records
 * are never overwritten — EPM's content for client 300 is materially thinner than the demo
 * fixture — but every record carries a `planSource` so the Command Center can state which
 * parts are plan-sourced and which are fixture-fill.
 *
 * TODO: back these with real initiative records (Firestore holds UX/workflow state) rather
 * than authored demo content.
 */

import { demoDate, demoMonthYear, demoDurationMonths, type DateOffset } from '../config/demoDates';
import type { ExecStatus, MilestoneStatus } from '../theme/decisionTokens';
import type { DecisionBrief } from './decisionBrief';
import { formatDriverValue, formatUsd, formatUsdRange } from '../roi/roiEngine';
import { roiForUseCase } from '../roi/useCaseRoi';
import { OPP_TO_UC } from './adapters/stage300FeaturedContent';
import {
  epmMilestonesForUseCase,
  epmRisksForUseCase,
  planSourceFor,
  type PlanSource,
} from './adapters/stage300ExecutionPlan';

export type ApprovalState = 'done' | 'current' | 'pending';
export type WorkstreamStatus = 'Not Started' | 'In Progress' | 'Complete' | 'Blocked';
export type RiskStatus = 'Open' | 'Mitigated' | 'Resolved';
/** 'Moderate' is the Execute-path spelling of the Evaluate register's 'Medium'. */
export type ExecRiskSeverity = 'Critical' | 'High' | 'Moderate' | 'Low';

export interface ApprovalStep {
  label: string;
  state: ApprovalState;
  /** Marks the CFO gate, which renders an orange mini-tag. */
  cfo?: boolean;
  note?: string;
}

export interface CfoAssumption {
  label: string;
  value: string;
  confidence: 'High' | 'Medium' | 'Low';
  source: string;
}

export interface CfoSummary {
  benefit: string;
  benefitNote?: string;
  cost: string;
  costNote?: string;
  payback: string;
  conf: string;
  assumptions: CfoAssumption[];
  /** Non-empty renders the amber "Flagged for validation" callout. */
  flags: string[];
}

export interface ChecklistItem {
  label: string;
  done: boolean;
}

export interface ExecMilestone {
  title: string;
  phase: string;
  owner: string;
  due: string;
  status: MilestoneStatus;
  pct: number;
  blocker?: string;
}

export interface Workstream {
  name: string;
  owner: string;
  status: WorkstreamStatus;
  /** May be a date or a word like "Ongoing" — never parse it. */
  target: string;
  notes: string;
}

export interface ExecRisk {
  title: string;
  severity: ExecRiskSeverity;
  likelihood: 'Low' | 'Medium' | 'High';
  owner: string;
  status: RiskStatus;
  description: string;
  mitigation: string;
}

export interface TeamMember {
  role: string;
  /** Empty when the seat is represented by title only (no stray demo names). */
  name?: string;
  title: string;
}

export interface InitiativeRecord {
  id: string;
  status: ExecStatus;
  phase: string;
  sponsor: string;
  owner: string;
  start: string;
  target: string;
  timeline: string;
  payback: string;
  confidence: string;
  objective: string;
  whyApproved: string;
  outcomes: string[];
  approval: ApprovalStep[];
  cfo: CfoSummary;
  checklist: ChecklistItem[];
  milestones: ExecMilestone[];
  /** null hides the Workstreams section (not yet planned). */
  workstreams: Workstream[] | null;
  risks: ExecRisk[];
  team: TeamMember[];
  /** Where this record's plan content came from. Resolved in getInitiativeRecord(). */
  planSource?: PlanSource;
}

// Initiative window as offsets from the demo anchor. Start ~5 weeks before the anchor
// (same month as the m1 kickoff) and target ~4 months after, giving a ~6-month window.
export const START_OFFSET: DateOffset = { weeks: -5 };
export const TARGET_OFFSET: DateOffset = { months: 4 };

// ============================================================
// FINANCIAL FIGURES COME FROM THE ROI ENGINE
//
// The CFO panel's four tiles, the hero Payback and ROI Confidence tiles, and any
// prose that quotes a figure all read src/prototype/roi. They used to be
// independently authored strings, which is how this file came to claim "$1.2M"
// investment while the four approval PDFs claimed "$2.1M – $3.4M", and how the
// same initiative carried "High (75%)" here and "85%" in the CFO summary.
//
// Authored content that is NOT a financial claim — sponsors, objectives,
// milestones, risks, team — is untouched.
// ============================================================

/** Formatted engine outputs for an initiative's base case. */
function fin(useCaseId: string) {
  const model = roiForUseCase(useCaseId);
  const base = model.scenarios.base;
  const conservative = model.scenarios.conservative;
  return {
    /** '$16.9M' — base-case annual midpoint. */
    annualMid: formatUsd(base.annualValueMid),
    /** '$15.2M – $18.6M' — the base-case band. */
    annualRange: formatUsdRange(base.annualValueLow, base.annualValueHigh),
    /** '$11.8M – $22.7M' — conservative low to aggressive high. */
    fullRange: formatUsdRange(model.annualValueRangeLow, model.annualValueRangeHigh),
    /** '$1.2M' */
    implementationCost: formatUsd(base.implementationCost),
    /** '$300K' */
    operatingCost: formatUsd(base.operatingCostPerYear),
    /** '$2.1M' */
    totalCost3Yr: formatUsd(base.totalCost3Yr),
    /** '3 months' / '3 mo' */
    payback: `${base.paybackMonths} months`,
    paybackShort: `${base.paybackMonths} mo`,
    conservativePayback: `${conservative.paybackMonths} months`,
    /** '87%' and 'High (87%)' */
    confidencePct: `${base.confidencePct}%`,
    confidenceFull: `${base.confidenceLabel} (${base.confidencePct}%)`,
    /** '$39.5M' */
    netValue3Yr: formatUsd(base.netValue3Yr),
  };
}

/**
 * The CFO assumptions table, built from the model's own inputs. Every row is
 * therefore an input the four headline figures actually consume — the previous
 * authored rows included "$9.0M annual revenue improvement from VBC", which no
 * surface's arithmetic used and which contradicted the model's $2.0M uplift.
 *
 * `extra` carries authored context rows that make no claim the engine owns
 * (organisation scale, measurement lag, and similar).
 */
function engineCfoAssumptions(useCaseId: string, extra: CfoAssumption[] = []): CfoAssumption[] {
  const model = roiForUseCase(useCaseId);
  return [
    ...model.drivers.map((driver) => ({
      label: driver.name.replace(/\s*\([^)]*\)\s*$/, ''),
      value: formatDriverValue(driver, driver.base),
      confidence: driver.confidence,
      source: driver.source,
    })),
    ...extra,
  ];
}

/**
 * Overwrite every financial field on a record with the engine's base case, so an
 * authored record cannot disagree with the model even by accident.
 * `cfo.assumptions` and `cfo.flags` are authored per record (built with
 * engineCfoAssumptions above) and preserved.
 */
function applyEngineFinancials(record: InitiativeRecord): InitiativeRecord {
  const f = fin(record.id);
  return {
    ...record,
    payback: f.payback,
    confidence: f.confidenceFull,
    cfo: {
      ...record.cfo,
      benefit: f.annualMid,
      benefitNote: 'base-case annual',
      cost: f.implementationCost,
      costNote: `+ ${f.operatingCost}/yr operating · ${f.totalCost3Yr} total 3-year cost`,
      payback: f.paybackShort,
      conf: f.confidencePct,
    },
  };
}

const AUTHORED_INITIATIVES: InitiativeRecord[] = [
  {
    id: 'opp-1',
    status: 'Active',
    phase: 'Implementation Planning',
    sponsor: 'COO',
    owner: 'Dana Whitfield · VP of Analytics',
    start: demoMonthYear(START_OFFSET),
    target: demoMonthYear(TARGET_OFFSET),
    timeline: `${demoDurationMonths(START_OFFSET, TARGET_OFFSET)} months`,
    payback: '',
    confidence: '',
    objective:
      'Reduce preventable readmissions and improve post-acute care coordination by deploying real-time transition tracking, risk stratification at discharge, and structured handoff protocols across care settings.',
    whyApproved:
      'Ranked #1 in the scoring model with a 99th-percentile business value score. The COO and CMO jointly sponsored this as a top strategic priority for FY2026. ROI is well-validated with high confidence, and the data infrastructure is ready to support deployment.',
    outcomes: [
      '8–12% reduction in avoidable 30-day readmissions across the targeted cohort',
      `${fin('opp-1').fullRange} annual savings across value-based contracts and avoidable costs`,
      'Care transition completion scores >85%',
    ],
    approval: [
      { label: 'Executive sponsor confirmed', state: 'done', note: `COO + CMO joint sponsorship · ${demoMonthYear({ months: -2 })}` },
      { label: 'Business case & ROI model submitted', state: 'done', note: demoMonthYear({ months: -2 }) },
      { label: 'CFO review & sign-off', state: 'done', cfo: true, note: `Signed off ${demoMonthYear({ months: -1 })} — ROI validated at ${fin('opp-1').confidencePct} model confidence, payback ${fin('opp-1').payback}` },
      { label: 'Board ratification', state: 'done', note: `${demoMonthYear({ months: -1 })} consent agenda` },
      { label: 'Initiative kickoff', state: 'done', note: `Kicked off ${demoDate(START_OFFSET)}` },
    ],
    // Financial fields are replaced by applyEngineFinancials(); the values here
    // are placeholders that never reach a screen.
    cfo: {
      benefit: '',
      cost: '',
      payback: '',
      conf: '',
      assumptions: engineCfoAssumptions('opp-1', [
        { label: 'Organization Net Patient Revenue', value: '$3.0B', confidence: 'High', source: 'Survey: Strategy Q5' },
        { label: 'Revenue addressable through VBC', value: '20%', confidence: 'High', source: 'Case study: large health system VBC 2022' },
      ]),
      flags: [],
    },
    checklist: [
      { label: 'Initiative owner assigned', done: true },
      { label: 'Executive sponsor confirmed', done: true },
      { label: 'Business case approved', done: true },
      { label: 'Baseline metrics confirmed', done: true },
      { label: 'Milestones drafted', done: true },
      { label: 'Risks reviewed', done: true },
      { label: 'Team mobilized', done: false },
    ],
    milestones: [
      { title: 'Executive Kickoff & Governance Setup', phase: 'Activation', owner: 'VP Care Transformation', due: demoDate({ weeks: -5 }), status: 'Complete', pct: 100 },
      { title: 'Current-State Workflow Assessment', phase: 'Discovery', owner: 'Clinical Lead', due: demoDate({ weeks: -2 }), status: 'Complete', pct: 100 },
      { title: 'Future-State Workflow Design & Approval', phase: 'Design', owner: 'Operational Lead', due: demoDate({ weeks: 2 }), status: 'In Progress', pct: 60 },
      { title: 'Data Pipeline & Analytics Enablement', phase: 'Build', owner: 'Analytics Lead', due: demoDate({ weeks: 5 }), status: 'Not Started', pct: 0 },
      { title: 'Pilot Launch — 2 Units', phase: 'Pilot', owner: 'Initiative Owner', due: demoDate({ weeks: 9 }), status: 'Not Started', pct: 0, blocker: 'Awaiting completion of workflow design milestone' },
      { title: 'Full Operational Rollout & KPI Validation', phase: 'Scale', owner: 'Initiative Owner', due: demoDate({ weeks: 17 }), status: 'Not Started', pct: 0 },
    ],
    workstreams: [
      { name: 'Governance & Oversight', owner: 'VP Care Transformation', status: 'In Progress', target: 'Ongoing', notes: 'Steering committee cadence established' },
      { name: 'Workflow Redesign', owner: 'Operational Lead', status: 'In Progress', target: demoDate({ weeks: 2 }), notes: 'Future-state design in review' },
      { name: 'Data & Analytics Enablement', owner: 'Analytics Lead', status: 'Not Started', target: demoDate({ weeks: 5 }), notes: 'Pending workflow design approval' },
      { name: 'Technology & EHR Integration', owner: 'IT / Integration Lead', status: 'Not Started', target: demoDate({ weeks: 7 }), notes: 'Scoping complete, build not started' },
      { name: 'Change Management & Training', owner: 'Change Management Lead', status: 'In Progress', target: demoDate({ weeks: 9 }), notes: 'Stakeholder mapping done; training plan in draft' },
      { name: 'Outcome Measurement', owner: 'Finance Partner', status: 'Not Started', target: demoDate({ weeks: 17 }), notes: 'Baseline methodology agreed' },
    ],
    risks: [
      { title: 'Frontline Staff Change Resistance', severity: 'High', likelihood: 'Medium', owner: 'Change Management Lead', status: 'Open', description: 'Nursing and case management staff may resist new handoff protocols and documentation requirements, slowing adoption.', mitigation: 'Early engagement with frontline champions, structured change management program, and visible leadership support.' },
      { title: 'EHR Workflow Complexity', severity: 'Moderate', likelihood: 'Medium', owner: 'IT / Integration Lead', status: 'Open', description: 'EHR discharge-workflow integration may extend the IT build timeline, compressing the pilot launch window.', mitigation: 'IT resource pre-committed in the project charter; escalation path established with CMIO if scope creep occurs.' },
      { title: 'Baseline Metric Availability', severity: 'Moderate', likelihood: 'Low', owner: 'Analytics Lead', status: 'Open', description: 'Readmission attribution data may be incomplete for the baseline period, affecting ROI measurement accuracy.', mitigation: 'Alternate data sources pre-identified; Finance aligned on measurement methodology.' },
      { title: 'Staffing Constraint — Pilot Units', severity: 'Low', likelihood: 'Low', owner: 'Operational Lead', status: 'Resolved', description: 'Two pilot units had planned staffing shortfalls due to seasonal volume; now resolved.', mitigation: 'Staffing plan confirmed with unit directors; contingency unit identified.' },
    ],
    team: [
      { role: 'Initiative Owner', name: 'Dana Whitfield', title: 'VP of Analytics' },
      { role: 'Operational Lead', name: 'Linda Suh', title: 'Director, Care Management' },
      { role: 'IT / Integration Lead', name: 'David Espinoza', title: 'VP, Clinical Informatics' },
    ],
  },

  {
    id: 'opp-2',
    status: 'CFO Review',
    phase: 'Approval',
    sponsor: 'CFO',
    owner: 'VP Pharmacy Services',
    start: 'Pending approval',
    target: '—',
    timeline: '6 months planned',
    payback: '',
    confidence: '',
    objective:
      'Risk-stratify chronic-disease panels and surface prescribing and medication interventions that close open care gaps — targeting adherence, statin therapy, and heart-failure GDMT measures inside the prescribing workflow.',
    whyApproved:
      `Ranked #3 on the composite with the widest ROI range in the featured set (${fin('opp-2').fullRange}). Shares risk-stratification and claims infrastructure with the Care Coordination initiative; proceeding together compounds value across the same cohorts.`,
    outcomes: [
      'Measurable movement on prescribing-linked HEDIS and CMS Stars measures within 2 quarters',
      'Care-gap recommendations live in the prescribing workflow for the phase-1 chronic cohorts',
      'Reduction in preventable ED visits and readmissions across the targeted panels',
    ],
    approval: [
      { label: 'Executive sponsor confirmed', state: 'done', note: `CFO + CMO joint sponsorship · ${demoMonthYear({ months: -1 })}` },
      { label: 'Business case & ROI model submitted', state: 'done', note: demoMonthYear() },
      { label: 'CFO review & sign-off', state: 'current', cfo: true, note: 'In review — 2 assumptions flagged for validation before sign-off' },
      { label: 'Board ratification', state: 'pending', note: `Targeted for the ${demoMonthYear({ months: 1 })} board cycle` },
      { label: 'Initiative kickoff', state: 'pending' },
    ],
    cfo: {
      benefit: '',
      cost: '',
      payback: '',
      conf: '',
      assumptions: engineCfoAssumptions('opp-2', [
        { label: 'Attributed chronic-cohort panel size', value: 'Diabetes, HF, COPD panels', confidence: 'Medium', source: 'Attribution-based panel definitions' },
        { label: 'Clinical model validation cost', value: '$250K', confidence: 'High', source: 'External validation proposal (included in implementation)' },
        { label: 'Conservative-case payback', value: fin('opp-2').conservativePayback, confidence: 'High', source: 'ROI engine (conservative scenario)' },
      ]),
      flags: [
        'Prescribing-adjacent recommendations require documented clinical validation and physician sign-off before go-live',
        'Quality-measure and attribution definitions must be reconciled across contracts before scoring',
      ],
    },
    checklist: [
      { label: 'Initiative owner assigned', done: true },
      { label: 'Executive sponsor confirmed', done: true },
      { label: 'Business case approved', done: false },
      { label: 'Baseline metrics confirmed', done: true },
      { label: 'Milestones drafted', done: true },
      { label: 'Risks reviewed', done: true },
      { label: 'Team mobilized', done: false },
    ],
    milestones: [
      { title: 'Cohort Definition & Measure Reconciliation', phase: 'Foundation', owner: 'VP Pharmacy Services', due: 'Weeks 1–4 post-kickoff', status: 'Not Started', pct: 0 },
      { title: 'Model Build & Clinical Validation', phase: 'Build', owner: 'Analytics Lead', due: 'Weeks 5–14', status: 'Not Started', pct: 0 },
      { title: '90-Day Cohort Pilot in the Prescribing Workflow', phase: 'Pilot', owner: 'VP Pharmacy Services', due: 'Months 4–6', status: 'Not Started', pct: 0 },
    ],
    workstreams: null,
    risks: [
      { title: 'Clinical model risk', severity: 'High', likelihood: 'Medium', owner: 'CMO', status: 'Open', description: 'Prescribing-adjacent recommendations attract clinical scrutiny; an unvalidated model will not clear governance.', mitigation: 'Documented validation and named physician sign-off before first live use.' },
      { title: 'Alert fatigue', severity: 'Moderate', likelihood: 'Medium', owner: 'VP Pharmacy Services', status: 'Open', description: 'Unmanaged recommendation volume erodes adoption faster than model error does.', mitigation: 'Set a per-prescriber volume budget before go-live and monitor dismissal rates weekly.' },
      { title: 'Cohort attribution drift', severity: 'Low', likelihood: 'Medium', owner: 'Analytics Lead', status: 'Open', description: 'The same patient can score differently across contracts if panel definitions are not reconciled.', mitigation: 'Lock attribution and measure specifications in the foundation phase.' },
    ],
    team: [
      { role: 'Initiative Owner', title: 'VP Pharmacy Services' },
      { role: 'Clinical Sponsor', title: 'Chief Medical Officer' },
      { role: 'Analytics Lead', name: 'Dana Whitfield', title: 'VP of Analytics' },
    ],
  },

  {
    id: 'opp-3',
    status: 'Activation Required',
    phase: 'Activation',
    sponsor: 'COO',
    owner: 'VP Ambulatory Operations',
    start: `${demoMonthYear({ months: 1 })} (planned)`,
    target: demoMonthYear({ months: 7 }),
    timeline: '6 months',
    payback: '',
    confidence: '',
    objective:
      'Match patients to the right specialist faster and close the referral loop — automated routing against current capacity, sub-specialty fit and network participation, with completion tracked end to end.',
    whyApproved:
      'Ranked #4 on the composite. The value case rests on retained specialty volume and the downstream procedures that follow it, and the initiative reuses the closed-loop coordination plumbing already being built for Care Coordination.',
    outcomes: [
      'Reduced referral leakage and retained specialty volume within 2 quarters',
      'Referral coordinators reclaim time lost to manual routing and status chasing',
      'Closed-loop completion status recorded for every outbound referral',
    ],
    approval: [
      { label: 'Executive sponsor confirmed', state: 'done', note: `COO · ${demoMonthYear({ months: -1 })}` },
      { label: 'Business case & ROI model submitted', state: 'done', note: demoMonthYear({ months: -1 }) },
      { label: 'CFO review & sign-off', state: 'done', cfo: true, note: `Signed off ${demoMonthYear()} — leakage baseline confirmed with Finance, base-case payback ${fin('opp-3').payback}` },
      { label: 'Board ratification', state: 'done', note: demoMonthYear() },
      { label: 'Initiative kickoff', state: 'current', note: `Scheduled ${demoDate({ weeks: 3 })} — team mobilization in progress` },
    ],
    cfo: {
      benefit: '',
      cost: '',
      payback: '',
      conf: '',
      assumptions: engineCfoAssumptions('opp-3', [
        { label: 'Retained specialty volume from in-network routing', value: 'Primary value driver', confidence: 'Medium', source: 'Peer benchmark: referral network programs' },
        { label: 'Leakage measurement lag', value: '~1 quarter (claims)', confidence: 'High', source: 'Claims availability assessment' },
      ]),
      flags: [],
    },
    checklist: [
      { label: 'Initiative owner assigned', done: true },
      { label: 'Executive sponsor confirmed', done: true },
      { label: 'Business case approved', done: true },
      { label: 'Baseline metrics confirmed', done: true },
      { label: 'Milestones drafted', done: true },
      { label: 'Risks reviewed', done: false },
      { label: 'Team mobilized', done: false },
    ],
    milestones: [
      { title: 'Leakage Baseline & Provider Directory Remediation', phase: 'Baseline', owner: 'Finance Partner', due: demoMonthYear({ months: 1 }), status: 'Not Started', pct: 0 },
      { title: 'Pilot — 3 High-Volume Specialties', phase: 'Pilot', owner: 'VP Ambulatory Operations', due: `${demoMonthYear({ months: 2 })}–${demoMonthYear({ months: 4 })}`, status: 'Not Started', pct: 0 },
      { title: 'Network-Wide Rollout + Scheduling Write-Back', phase: 'Scale', owner: 'Initiative Owner', due: `${demoMonthYear({ months: 5 })}–${demoMonthYear({ months: 7 })}`, status: 'Not Started', pct: 0 },
    ],
    workstreams: null,
    risks: [
      { title: 'Referral steering sensitivity', severity: 'Moderate', likelihood: 'Medium', owner: 'Chief Legal Officer', status: 'Open', description: 'Matching logic must be clinically and contractually defensible, and must preserve patient choice.', mitigation: 'Document the matching criteria and route them through clinical and legal review before go-live.' },
      { title: 'Specialist adoption', severity: 'Moderate', likelihood: 'Medium', owner: 'VP Ambulatory Operations', status: 'Open', description: 'Without early engagement the network reverts to established referral habits within a quarter.', mitigation: 'Engage the top-referring PCPs and receiving specialists during the pilot design.' },
      { title: 'Provider directory accuracy', severity: 'Low', likelihood: 'Medium', owner: 'VP Data Engineering', status: 'Open', description: 'Sub-specialty, location and network-participation data are inconsistent across systems.', mitigation: 'Directory remediation is the first baseline milestone; fall back to specialty-level routing until it clears.' },
    ],
    team: [
      { role: 'Initiative Owner', title: 'VP Ambulatory Operations' },
      { role: 'Executive Stakeholder', title: 'Chief Medical Officer' },
      { role: 'Analytics Lead', name: 'Dana Whitfield', title: 'VP of Analytics' },
    ],
  },
];

/**
 * The authored records with every financial field replaced by the engine's base
 * case. Nothing downstream reads AUTHORED_INITIATIVES directly.
 */
const INITIATIVES: InitiativeRecord[] = AUTHORED_INITIATIVES.map(applyEngineFinancials);

const BY_ID = new Map(INITIATIVES.map((rec) => [rec.id, rec]));

/** Maps an Evaluate risk severity onto the Execute register's spelling. */
const toExecSeverity = (s: string): ExecRiskSeverity => (s === 'Medium' ? 'Moderate' : (s as ExecRiskSeverity));

/**
 * Scaffold for a use case just proceeded from Evaluate: nothing has been approved yet, so
 * the chain starts at sponsor assignment and the ROI figures carry over from the brief.
 */
export function makePending(brief: DecisionBrief): InitiativeRecord {
  // Where the stage execution-plan pipeline has run for this use case, its milestones and risk
  // register are real plan output and beat the brief-derived approximation. Where it has not,
  // the brief still supplies the scaffold.
  const useCaseId = OPP_TO_UC[brief.id];
  const epmMilestones = epmMilestonesForUseCase(useCaseId);
  const epmRisks = epmRisksForUseCase(useCaseId);

  return {
    id: brief.id,
    status: 'Approval Pending',
    phase: 'Approval intake',
    sponsor: 'To be assigned',
    owner: 'To be assigned',
    start: '—',
    target: '—',
    timeline: brief.ttv,
    payback: '—',
    confidence: '—',
    objective: brief.description,
    whyApproved: brief.decisionText,
    outcomes: [brief.opImpact],
    approval: [
      { label: 'Executive sponsor confirmed', state: 'current', note: 'Sponsor assignment in progress' },
      { label: 'Business case & ROI model submitted', state: 'pending' },
      { label: 'CFO review & sign-off', state: 'pending', cfo: true },
      { label: 'Board ratification', state: 'pending' },
      { label: 'Initiative kickoff', state: 'pending' },
    ],
    // An uncosted initiative gets the engine's annual value — that figure derives
    // from its own evaluation estimate and is honest. Investment, payback and
    // confidence stay '—': the engine would happily compute them off the default
    // canon schedule, and quoting a payback for a budget nobody has costed would
    // be exactly the kind of borrowed precision this work exists to remove.
    cfo: {
      benefit: fin(brief.id).fullRange,
      benefitNote: 'annual range (evaluation estimate)',
      cost: 'TBD',
      costNote: 'business case in preparation',
      payback: '—',
      conf: '—',
      assumptions: [],
      flags: ['Business case not yet submitted — ROI figures carry over from the evaluation model'],
    },
    checklist: [
      { label: 'Initiative owner assigned', done: false },
      { label: 'Executive sponsor confirmed', done: false },
      { label: 'Business case approved', done: false },
      { label: 'Baseline metrics confirmed', done: false },
      { label: 'Milestones drafted', done: false },
      { label: 'Risks reviewed', done: true },
      { label: 'Team mobilized', done: false },
    ],
    milestones: epmMilestones.length
      ? epmMilestones
      : brief.roadmap.map((phase) => ({
          title: phase.desc,
          phase: phase.phase,
          owner: 'TBD',
          due: phase.dur,
          status: 'Not Started' as MilestoneStatus,
          pct: 0,
        })),
    workstreams: null,
    risks: epmRisks.length
      ? epmRisks
      : brief.risks.map((risk) => ({
          title: risk.name,
          severity: toExecSeverity(risk.severity),
          likelihood: (risk.likelihood === 'Very High' ? 'High' : risk.likelihood === 'Very Low' ? 'Low' : risk.likelihood) as ExecRisk['likelihood'],
          owner: risk.owner,
          status: 'Open' as RiskStatus,
          description: risk.description,
          mitigation: risk.mitigation,
        })),
    team: [{ role: 'Initiative Owner', title: 'To be assigned' }],
  };
}

/** True when this initiative has an authored record rather than a pending scaffold. */
export function hasInitiativeRecord(id: string): boolean {
  return BY_ID.has(id);
}

/**
 * The record for an initiative: the authored one when it exists, otherwise a pending
 * scaffold derived from its evaluation brief (or from the stage execution plan where one
 * exists). Either way the record carries the provenance of its plan content.
 */
export function getInitiativeRecord(id: string, brief: DecisionBrief): InitiativeRecord {
  const authored = BY_ID.get(id);
  const record = authored ?? makePending(brief);
  return { ...record, planSource: planSourceFor(OPP_TO_UC[id], Boolean(authored)) };
}
